import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { K } from '../kabinet/atoms';
import { склонение } from '../sklonenie';
import { форматЧисла } from '../ui/chisla';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * КАПСУЛЫ СПП И ЗАПАСА — ОДНОЙ ЧАСТИЦЕЙ НА ЧЕТЫРЕ МЕСТА.
 *
 * Передача дизайнера 07.09.2026, раздел 1. Они стоят в шапке экрана
 * кампании, в строке списка, в карточке на 390 и в разборе по товарам.
 * Четыре копии разошлись бы после первой правки — и разошлись бы тихо:
 * цвет капсулы никто не сверяет глазом с числом рядом.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЦВЕТ ПРИХОДИТ ИЗ СОСТОЯНИЯ, А НЕ ИЗ ЧАСТИЦЫ, И ЭТО НЕ УПРОЩЕНИЕ.
 *
 * Светофор запаса — пять состояний одного узла, а эталон рисует их
 * выражением (`background:{{ c.stockBg }}`). В отрисованном снимке
 * остаётся ОДНО значение — то, что выпало стенду. Вырежи его в частицу,
 * и все пять состояний станут одного цвета.
 *
 * Поэтому частица держит размеры и поля, а краску кладём здесь —
 * из тех же переменных оформления, по ступени запаса.
 * ═══════════════════════════════════════════════════════════════════════
 */

export type СтупеньЗапаса = 'много' | 'норма' | 'мало' | 'кончается' | 'неизвестно';

export interface Запас {
  штук: number;
  дней: number | null;
  ступень: СтупеньЗапаса;
  за30: number;
  /**
   * Половины суммы: склады Wildberries и склады продавца.
   *
   * `null` или отсутствие — карточка снята до 09.09.2026, когда половины
   * начали храниться врозь. Всплывашка тогда показывает только сумму:
   * нарисовать «0 шт» там, где мы не знаем, — это соврать.
   */
  wb?: number | null;
  свои?: number | null;
  at?: string | null;
  nmId?: number;
}

/**
 * Цвета ступеней — взяты с эталона замером, а не из таблицы документа.
 *
 * РАЗНОЧТЕНИЕ ЗАПИСАНО. Таблица в ТЗ ставит «больше 30 дней»
 * в нейтральный цвет, а точку зелёной; эталон делает наоборот, и он
 * согласен сам с собой дважды — так считает его код и так же говорит
 * его собственный комментарий. Решает эталон: приёмка по разделу 5
 * сверяется «замером с эталона».
 *
 * И главное: точка в эталоне красится `currentColor`, то есть своего
 * цвета у неё нет физически — строка «точка --good, текст --ink-2»
 * такой разметкой не выражается вовсе.
 */
const ints = форматЧисла();

/** «день · дня · дней» — три формы одной записью, как везде в кабинете. */
const ДНИ = Object.freeze(['день', 'дня', 'дней'] as const);

const ЦВЕТА: Record<СтупеньЗапаса, { фон: string; текст: string }> = {
  много: { фон: 'var(--good-wash)', текст: 'var(--good-ink)' },
  норма: { фон: 'var(--bg-active)', текст: 'var(--ink-2)' },
  мало: { фон: 'var(--warn-wash)', текст: 'var(--warn-ink)' },
  кончается: { фон: 'var(--bad-wash)', текст: 'var(--bad-ink)' },
  неизвестно: { фон: 'var(--bg-active)', текст: 'var(--ink-2)' },
};

/**
 * «8 шт на 13 дней» — предлогом, а не точкой.
 *
 * «8 шт · 13 дней» читалось как два независимых числа; предлог связывает
 * их в одно утверждение. Слова «остаток» в капсуле нет — оно занимало
 * треть строки и ничего не добавляло, смысл несёт подсказка.
 */
export function подписьЗапаса(з: Запас, короткая: boolean): string {
  const штук = `${ints.format(з.штук)} шт`;
  if (короткая || з.дней === null) return штук;
  return `${штук} на ${з.дней} ${склонение(з.дней, ДНИ)}`;
}

/** Подсказка остатка — теми же числами, по которым посчитан цвет. */
export function подсказкаЗапаса(з: Запас, где: 'товар' | 'кампания' | 'все-товары'): string {
  const начало =
    где === 'все-товары'
      ? `Остаток по всем товарам кампании: ${ints.format(з.штук)} шт.`
      : где === 'кампания'
        ? `Общий остаток товара на складах WB и продавца: ${ints.format(з.штук)} шт.`
        : `Общий остаток на складах WB и продавца: ${ints.format(з.штук)} шт.`;
  if (з.дней === null) {
    return `${начало} Заказов за 30 суток не было — на сколько хватит, посчитать не из чего.`;
  }
  /* СЧЁТ «НА СКОЛЬКО ХВАТИТ» БЕРЁТСЯ У `фразаЗапаса`, А НЕ ПОВТОРЯЕТСЯ.
     Две копии одного счёта разошлись бы после первой правки, и разошлись
     бы тихо: подсказка и всплывашка стоят в разных местах экрана,
     и рядом их числа никто не видит. Начало у подсказки своё — оно
     называет, чей это остаток. */
  const хвост =
    з.дней <= 14
      ? ' Успеть завезти или снизить ставку — иначе реклама поведёт на кончившийся товар.'
      : '';
  const разбивка =
    где === 'все-товары' ? ' Разбивка по товарам — во вкладке «Деньги и товары».' : '';
  const счёт = фразаЗапаса(з).replace(/^Всего [^.]+\. /, '');
  return `${начало} ${счёт}${хвост}${разбивка}`;
}

/**
 * Фраза про запас — та же, что во второй половине подсказки.
 *
 * ОДНА ФУНКЦИЯ НА ПОДСКАЗКУ И ВСПЛЫВАШКУ: два счёта «на сколько
 * хватит» разошлись бы после первой правки, и разошлись бы тихо —
 * числа стоят в разных местах экрана и рядом их никто не видит.
 */
export function фразаЗапаса(з: Запас): string {
  const всего = `Всего ${ints.format(з.штук)} шт.`;
  if (з.дней === null) {
    return `${всего} Заказов за 30 суток не было — на сколько хватит, посчитать не из чего.`;
  }
  const вСутки = (з.за30 / 30).toFixed(1).replace('.', ',');
  return (
    `${всего} Заказов за 30 суток — ${з.за30}, это ${вСутки} в день: ` +
    `хватит примерно на ${з.дней} ${склонение(з.дней, ДНИ)}.`
  );
}

/**
 * День продаж словами: «7 сентября».
 *
 * БЕЗ ГОДА И БЕЗ ЧАСОВ: окно у СПП — тридцать суток, за год выйти
 * некуда, а часы у суточной строки бессмысленны.
 */
export function деньСловами(iso: string | null): string {
  if (iso === null || iso === '') return '';
  const м = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (м === null) return '';
  const МЕСЯЦЫ = [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря',
  ];
  const месяц = МЕСЯЦЫ[Number(м[2]) - 1];
  if (месяц === undefined) return '';
  return `${Number(м[3])} ${месяц}`;
}

/**
 * Подсказка СПП.
 *
 * ДЕНЬ НАЗЫВАЕТСЯ, ЕСЛИ ИЗВЕСТЕН. 08.09.2026 владелец просил показывать
 * не среднее за месяц, а «какая сегодня». Показываем последний день
 * с продажами — и говорим, какой это был день: «31 %» без даты выглядит
 * как «сейчас», а у товара, не продававшегося неделю, это позавчерашняя
 * правда.
 */
export function подсказкаСпп(
  процент: number,
  где: 'товар' | 'кампания',
  когда: string | null = null,
): string {
  const кто = где === 'кампания' ? 'по товару кампании' : 'по этому товару';
  const день = деньСловами(когда);
  const источник =
    день === '' ? 'Посчитана по последним продажам' : `Посчитана по продажам за ${день}`;
  return `Скидка постоянного покупателя ${кто}: ${процент} %. ${источник} — это разница между ценой продавца и тем, что заплатил покупатель; разницу возмещает Wildberries. Она влияет на цену в карточке, а значит и на заказы.`;
}

/**
 * Капсула СПП.
 *
 * ДВА ВИДА, А НЕ ОДИН: в шапке экрана она высотой `--tag-h` и пишет
 * «СПП <b>12 %</b>»; в строке и в карточке — восемнадцать точек
 * и приглушённое слово перед числом. Так в эталоне, и по делу: в шапке
 * она соседствует с состоянием кампании, в строке — с числом товаров.
 */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * СПП СКРЫТА С ЭКРАНА. ОДНИМ МЕСТОМ, А НЕ ЧЕТЫРЬМЯ.
 *
 * 09.09.2026 владелец: «сейчас лучше скрыть СПП с сайта вообще,
 * функционал оставить, пусть будет, но пока скрыть».
 *
 * ПОЧЕМУ ОН ПРАВ. Числа, которое можно показать честно, у нас нет.
 * Разведка 09.09 показала: витрину, где живёт цена покупателя, серверу
 * закрыли (403 на всех путях и с любого выхода), а из продаж и заказов
 * восстанавливается ДРУГАЯ величина — СПП конкретных покупателей с их
 * личными историями (26…38 % по одному товару при неизменной цене),
 * тогда как новому покупателю в тот же момент показывали 45 %.
 * Капсула показывала 32 % так, будто это сегодняшнее число.
 *
 * РУБИЛЬНИК ЗДЕСЬ, А НЕ В ЧЕТЫРЁХ МЕСТАХ ПОКАЗА. Убрать капсулу
 * из шапки, строки, карточки и разбора значило бы четыре правки —
 * и четыре места, где её забудут вернуть. Частица возвращает пустоту
 * сама, ровно как она делает это при отсутствии числа.
 *
 * ЧТО ОСТАЁТСЯ ЖИВЫМ: колонка `products.spp`, счёт по продажам, поле
 * в ответе службы, подсказка и эта частица. Появится источник —
 * снимается одна строка.
 * ═══════════════════════════════════════════════════════════════════════
 */
export const СПП_СКРЫТА = true;

export function Спп({
  процент,
  где,
  когда = null,
}: {
  /**
   * ПУСТОТУ ЧАСТИЦА РЕШАЕТ САМА — И ЭТО ИСПРАВЛЕНИЕ ПАДЕНИЯ.
   *
   * 08.09.2026 снимок поймал: экран кампании падал целиком —
   * «Cannot read properties of undefined (reading 'ступень')», и вместо
   * кампании человек видел «Не удалось показать: кабинет».
   *
   * УСЛОВИЕ СТОЯЛО В РАЗМЕТКЕ, И СТОЯЛО В ЧЕТЫРЁХ МЕСТАХ: `spp !== null`
   * в шапке, в строке, в карточке и в разборе. А поля в ответе могло
   * не быть СОВСЕМ — так отвечает служба прежней сборки, и минуту-другую
   * после выкатки так отвечает настоящая. `undefined !== null` истинно,
   * и капсула получала пустоту, которую тут же читала.
   *
   * ЧЕТЫРЕ ОДИНАКОВЫХ УСЛОВИЯ — ЭТО ЧЕТЫРЕ МЕСТА, ГДЕ ОШИБИТЬСЯ. Теперь
   * их ноль: частица принимает пустоту как законное значение и молчит.
   * Ронять экран из-за отсутствия справки о товаре — значит менять
   * «нет справки» на «сервис не работает».
   */
  процент: number | null | undefined;
  где: 'шапка' | 'строка';
  /** День, по продажам которого посчитано. `null` — день неизвестен. */
  когда?: string | null | undefined;
}) {
  if (СПП_СКРЫТА) return null;
  if (процент === null || процент === undefined) return null;
  const подпись = подсказкаСпп(процент, где === 'шапка' ? 'кампания' : 'товар', когда ?? null);
  if (где === 'шапка') {
    return (
      <span data-d="спп-кампании" title={подпись} className={K.сппКампании}>
        СПП <b className={K.числоСппКампании}>{процент} %</b>
      </span>
    );
  }
  return (
    <span data-d="спп-товара" title={подпись} className={K.сппТовара}>
      <span className={K.словоСпп}>СПП</span>
      {процент} %
    </span>
  );
}

/**
 * Капсула запаса со светофором.
 *
 * ТОЧКИ НЕТ, КОГДА ЗАПАС НЕ ПОСЧИТАН. Заказов за 30 суток не было —
 * делить не на что, и цветной кружок обещал бы смысл, которого нет.
 * Так в передаче дословно.
 */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * ВСПЛЫВАШКА ОСТАТКА — ДВА СКЛАДА И ФРАЗА ПРО ЗАПАС.
 *
 * 09.09.2026 владелец прислал вид: «На складах WB 16 шт · На ваших
 * складах 21 шт», под чертой — «Всего 37 шт. Заказов за 30 суток — 18,
 * это 0,6 в день: хватит примерно на 62 дня».
 *
 * ПОЧЕМУ РАЗБИВКА ВАЖНА, А НЕ КРАСИВА. Со склада Wildberries товар
 * уезжает к покупателю за сутки, со склада продавца — за неделю.
 * Тридцать семь штук, из которых тридцать одна лежит у продавца, —
 * это совсем не тот запас под рекламу, что тридцать семь на складе WB,
 * а светофор у них один.
 *
 * ПОЛОВИН МОЖЕТ НЕ БЫТЬ: карточка, снятая до этой правки, знает только
 * сумму. Тогда рисуется одна фраза, а не «0 шт» — ноль там, где мы
 * не знаем, это ложь, и как раз в опасную сторону.
 *
 * РИСУЕТСЯ ПОРТАЛОМ В КОРЕНЬ СТРАНИЦЫ. Капсула стоит в строке таблицы
 * с прокруткой; всё, что выходит за край такого родителя, обрезается.
 * На этом уже обжигались с увеличением фото (`thumb.tsx`), и там же
 * записан довод.
 * ═══════════════════════════════════════════════════════════════════════
 */
function ВсплывашкаЗапаса({ запас, где }: { запас: Запас; где: { left: number; top: number } }) {
  const есть = типЧисла(запас.wb) !== null || типЧисла(запас.свои) !== null;
  return createPortal(
    /* ═══════════════════════════════════════════════════════════════════
       `data-kab` НА ОБЁРТКЕ, А НЕ НА САМОЙ ВСПЛЫВАШКЕ.

       Правила кабинета объявлены как `[data-kab] .k-…` — то есть класс
       обязан быть ПОТОМКОМ узла с атрибутом. Пока атрибут стоял на том же
       узле, что и класс, ни одно правило не применялось: всплывашка
       выходила обычной строкой в потоке `body`, без `position: fixed`,
       без фона и без тени — то есть внизу страницы и невидимой. Владелец
       10.09.2026: «всплывашка при наведении не работает».

       ЭТО ВТОРОЙ РАЗ. Ровно на этом же 09.09 обожглась плашка
       уведомления, и там же в `ui/toast.tsx` записан довод. Знание было —
       и лежало в комментарии, а не в наборе. Теперь стережёт
       `ui/data-kab-ne-na-chastitse.spec.ts`.

       ОБЁРТКА НИЧЕГО НЕ ВЕСИТ: `display: contents` убирает её из потока
       целиком, а сама всплывашка прибита к окну.
       ═══════════════════════════════════════════════════════════════ */
    <span data-kab="" style={{ display: 'contents' }}>
      <span
        data-d="всплывашка-остатка"
        role="tooltip"
        className="k-всплывашка-остатка"
        style={{ left: где.left, top: где.top }}
      >
        {есть && (
          <span className="k-склады-остатка">
            <span className="k-склад-подпись">На складах WB</span>
            <span className="k-склад-подпись">На ваших складах</span>
            <span data-d="остаток-складов-вб" className="k-склад-число">
              {штуками(запас.wb)}
            </span>
            <span data-d="остаток-своих-складов" className="k-склад-число">
              {штуками(запас.свои)}
            </span>
          </span>
        )}
        <span data-d="запас-словами" className="k-запас-словами">
          {фразаЗапаса(запас)}
          <span className="block rk-text-xs rk-faint">{запас.nmId ? `Артикул WB ${запас.nmId}. ` : ''}{stockObservation(запас.at)}</span>
        </span>
      </span>
    </span>,
    document.body,
  );
}

/** Число или пустота — `0` законное значение, а `null` значит «не знаем». */
function типЧисла(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** «16 шт» либо «—», когда половина не выгружена. */
function штуками(v: number | null | undefined): string {
  const ч = типЧисла(v);
  return ч === null ? '—' : `${ints.format(ч)} шт`;
}

/**
 * Капсула запаса со светофором.
 *
 * ТОЧКИ НЕТ, КОГДА ЗАПАС НЕ ПОСЧИТАН. Заказов за 30 суток не было —
 * делить не на что, и цветной кружок обещал бы смысл, которого нет.
 * Так в передаче дословно.
 */
export function ЗапасКапсула({
  запас,
  где,
  короткая = false,
}: {
  /** Пустоту частица решает сама — довод целиком у `Спп` выше. */
  запас: Запас | null | undefined;
  где: 'шапка' | 'строка';
  /** В списке кампаний дней не пишем: место в колонке. */
  короткая?: boolean;
}) {
  const узел = useRef<HTMLSpanElement>(null);
  const [место, поставить] = useState<{ left: number; top: number; identity: string } | null>(null);
  const identity=`${запас?.nmId}:${запас?.штук}:${запас?.at}`;
  useEffect(()=>{
    if (место===null) return;
    const close=()=>поставить(null);
    window.addEventListener('scroll',close,true);window.addEventListener('resize',close);
    return ()=>{window.removeEventListener('scroll',close,true);window.removeEventListener('resize',close);};
  },[место]);

  if (запас === null || запас === undefined) return null;
  const ц = ЦВЕТА[запас.ступень];
  const куда = где === 'шапка' ? 'кампания' : 'товар';

  /* РАЗВОРАЧИВАЕТСЯ У КРАЯ ЭКРАНА. Всплывашка, вылезшая за окно, ничем
     не лучше обрезанной — тот же довод, что у увеличения фото. */
  const открыть = () => {
    const r = узел.current?.getBoundingClientRect();
    if (!r) return;
    const ШИРИНА = 300;
    const ВЫСОТА = 140;
    const влезает = r.left + ШИРИНА < window.innerWidth - 8;
    поставить({
      identity,
      left: влезает ? r.left : Math.max(8, window.innerWidth - ШИРИНА - 8),
      top:
        r.bottom + 8 + ВЫСОТА < window.innerHeight ? r.bottom + 8 : Math.max(8, r.top - ВЫСОТА - 8),
    });
  };

  return (
    <span
      ref={узел}
      data-d="остаток-товара"
      /* ПОДПИСЬ БРАУЗЕРА СНЯТА: две подсказки на одном узле мешают друг
         другу, а `title` не умеет показать разбивку по складам. Слова
         для чтения с экрана остаются в `aria-label`. */
      aria-label={подсказкаЗапаса(запас, куда)}
      className={где === 'шапка' ? K.запасШапки : K.запасТовара}
      style={{ background: ц.фон, color: ц.текст }}
      onMouseEnter={открыть}
      onMouseLeave={() => поставить(null)}
      /* НА КАСАНИИ НАВЕДЕНИЯ НЕТ ВОВСЕ, поэтому нажатие переключает:
         иначе на телефоне всплывашки не увидеть ни разу. */
      onClick={(e) => {
        e.stopPropagation();
        if (window.matchMedia('(hover: hover)').matches || место === null || место.identity!==identity) открыть();
        else поставить(null);
      }}
    >
      {запас.дней !== null && <span aria-hidden="true" className={K.точкаЗапаса} />}
      {подписьЗапаса(запас, короткая)}
      {место !== null && место.identity===identity && <ВсплывашкаЗапаса запас={запас} где={место} />}
    </span>
  );
}

const stockTime = new Intl.DateTimeFormat('ru-RU',{dateStyle:'short',timeStyle:'short',timeZone:'Europe/Moscow'});
export function stockObservation(at: string | null | undefined): string {
  const ms=at?Date.parse(at):NaN;
  if (!Number.isFinite(ms)) return 'Время обновления остатков неизвестно.';
  const time=stockTime.format(new Date(ms));
  return `Обновлено ${time} МСК.${Date.now()-ms>36*3_600_000?' Данные устарели.':''}`;
}
